import RecruitApiClient from '../../RecruitApiClient';
import {
  IAddTaskParams,
  IApplication,
  IApplicationTask,
  IAttachment,
  IEmailTemplate,
  IEmailTemplateList,
  IFormResponseListItem,
  IFormsOption,
  IInviteByEmailParams,
  IPreviewData,
  ITimestamp,
  IVideoInterview,
  IFit2WorkExportParams,
  IRevolveExportParams,
  IXRefExportParams,
  IReferooExportParams,
  IFoundUExportParams,
  IHronboardExportParams,
  IRoublerExportParams,
  IHronboardResponse,
  IIntegrationsResponse,
  ISendExternalEmailParams,
  ISparkHireExportParams,
  IAlayaCareExportParams,
  IReferooData,
  ISparkHireData,
  IGetSmartFormsParams,
  IGetEventsParams,
  IFit2WorkData,
  ICognologyExportParams,
  ICognologyData,
  IElmoExportParams,
  IIntelliHrData
} from './types';
import { IJob } from '../EditJob/types';
import { IActivity } from '../Candidate/types';
import { IApprovalTemplate } from '../ApprovalForms/ApprovalTemplates/types';
import { IApplicantQuestion } from '../Components/Utilities/QuestionFieldGenerator';
import { IEvent, IEventType, IRecruiter } from '../../ScoutCalendar/types';
import { IAnswer, IApprovalForm } from '../ApprovalForms/types';
import dayjs from 'dayjs';
import { ICountries } from '../Components/sharedTypes';
class Api {
  getApplicationData = (
    jobId: number,
    applicationId: number,
    index: string
  ): Promise<{ res: IApplication }> =>
    RecruitApiClient.get(`/api/v4/jobs/${jobId}/applications/${applicationId}?i=${index}`, null);
  getApplicationStatuses = (jobId: number) =>
    RecruitApiClient.get(`/api/v4/jobs/${jobId}/job_application_statuses`, null);
  updateApplicationStatus = (jobId: number, applicationId: number, statusId: number) =>
    RecruitApiClient.put(
      `/api/v4/jobs/${jobId}/applications/${applicationId}/application_status/${statusId}`
    );
  updateCandidateLinkedInURL = (candidateId: number, linkedInURL: string) =>
    RecruitApiClient.put(`/api/v4/candidates/${candidateId}`, {
      candidate: { linkedin_url: linkedInURL }
    });
  getJobsOptions = (jobId: number) =>
    RecruitApiClient.get(`/api/v4/jobs/${jobId}/bulk_move_applications`, null);
  getCsvExportTemplates = () => RecruitApiClient.get(`/api/v4/csv_export_templates`, null);
  getCandidateDataWithCsvTemplate = (jobId: number, applicationIds: number[], templateId: number) =>
    RecruitApiClient.get(
      `/api/v4/jobs/${jobId}/applications/export_to_csv?template_id=${templateId}&application_ids=${applicationIds.join(',')}`,
      null
    );
  moveApplication = (jobId: number, param: { target_job: string; application_ids: number[] }) =>
    RecruitApiClient.post(`/api/v4/jobs/${jobId}/bulk_move_applications`, param);
  trashApplication = (jobId: number, applicationId: number) =>
    RecruitApiClient.delete(`/api/v4/jobs/${jobId}/applications`, {
      application_ids: [applicationId]
    });
  updateApplicationNotepad = (jobId: number, applicationId: number, note: string) =>
    RecruitApiClient.put(`/api/v4/jobs/${jobId}/applications/${applicationId}/update_notepad`, {
      suitability_comment: note
    });
  sendApplicationSummary = (jobId: number, applicationId: number, emails: string[]) =>
    RecruitApiClient.post(
      `/api/v4/jobs/${jobId}/applications/${applicationId}/send_individual_summary`,
      { emails }
    );
  updateSummary = (jobId: number, applicationId: number) =>
    RecruitApiClient.post(
      `/api/v4/jobs/${jobId}/applications/${applicationId}/application_summary`
    );
  getHrQuestions = (jobId: number, applicationId: number) =>
    RecruitApiClient.get(`/api/v4/jobs/${jobId}/applications/${applicationId}/hr_answers`);
  saveHrQuestion = (jobId: number, applicationId: number, questionId: number, params: any) =>
    RecruitApiClient.put(
      `/api/v4/jobs/${jobId}/applications/${applicationId}/hr_answers/${questionId}`,
      { hr_answer_set: params }
    );
  getRatingSetForApplication = (jobId: number, applicationId: number) =>
    RecruitApiClient.get(`/api/v4/jobs/${jobId}/applications/${applicationId}/rating_set`);
  resetRatingForApplication = (jobId: number, applicationId: number) =>
    RecruitApiClient.post(`/api/v4/jobs/${jobId}/applications/${applicationId}/reset_rating`);
  rateResponses = (jobId: number, applicationId: number, params: any) =>
    RecruitApiClient.post(`/api/v4/jobs/${jobId}/applications/${applicationId}/rating_sets`, {
      rating_set: params
    });
  cloneApplication = (
    jobId: number,
    application_id: number,
    param: { to_job_id: string; cloning_assets: boolean }
  ) => {
    return RecruitApiClient.post(
      `/api/v4/jobs/${jobId}/applications/${application_id}/clone_application`,
      param
    );
  };
  resendApplication = (jobId: number, applicationId: number) =>
    RecruitApiClient.post(`/api/v4/jobs/${jobId}/applications/${applicationId}/resend_application`);
  deleteApplication = (jobId: number, applicationId: number) =>
    RecruitApiClient.delete(`/api/v4/jobs/${jobId}/applications/${applicationId}`);
  getSearchIndex = (jobId: number, applicationId: number) =>
    RecruitApiClient.get(`/api/v4/jobs/${jobId}/applications/${applicationId}/search_index`, null);
  updateSearchIndex = (jobId: number, applicationId: number) =>
    RecruitApiClient.put(`/api/v4/jobs/${jobId}/applications/${applicationId}/search_index`, {});
  deleteSearchIndex = (jobId: number, applicationId: number) =>
    RecruitApiClient.delete(`/api/v4/jobs/${jobId}/applications/${applicationId}/search_index`);
  updateApplicationRating = (
    jobId: number,
    applicationId: number,
    param: { ref_check_rating: string | null; interview_rating: string | null }
  ) => {
    return RecruitApiClient.put(`/api/v4/jobs/${jobId}/applications/${applicationId}`, param);
  };
  updateUiPreferences = (param: { sorting: { order: string[] }; source: string }) => {
    return RecruitApiClient.post(`/api/v4/ui_preferences`, { ui_preference: param });
  };
  getUiPreferences = () => RecruitApiClient.get(`/api/v4/ui_preferences?source=application`, null);
  addRecord = (jobId: number, applicationId: number, params: { subject: string; body: string }) =>
    RecruitApiClient.post(`/api/v4/jobs/${jobId}/applications/${applicationId}/permanent_record`, {
      note: params
    });
  getEvents = (params: IGetEventsParams) => RecruitApiClient.get('/api/v4/calendar/events', params);
  getSmsTemplates = () => RecruitApiClient.get('/api/v4/sms_templates', {});
  getSmsTemplatePlaceholders = (kind?: string) =>
    RecruitApiClient.get('/api/v4/sms_templates/placeholders', { kind });
  saveSmsTemplate = (smsTemplateName: string, smsMessage: string) =>
    RecruitApiClient.post(`/api/v4/sms_templates`, {
      sms_template: { title: smsTemplateName, body: smsMessage }
    });

  getSubEntityGroupedEmailTemplates = (jobId: number): Promise<{ res: IEmailTemplateList }> =>
    RecruitApiClient.get(`/api/v4/jobs/${jobId}/email_templates`, { group_templates: true });

  getEmailLetterheadGroupedEmailTemplates = (type?: string): Promise<{ res: IEmailTemplateList }> =>
    RecruitApiClient.get(`/api/v4/calendar/email_templates/grouped_email_templates`, { type });

  getEmailTemplateFromCalendar = (templateId: number): Promise<{ res: IEmailTemplate }> =>
    RecruitApiClient.get(`/api/v4/calendar/email_templates/${templateId}`, {});
  getEmailTemplateFromJob = (templateId: number, jobId: number): Promise<{ res: IEmailTemplate }> =>
    RecruitApiClient.get(`/api/v4/jobs/${jobId}/email_templates/${templateId}`, {});
  addToEventSms = (
    jobId: number,
    eventId: number,
    applicationIds: number[],
    content: string,
    smsTemplateId?: number
  ) =>
    RecruitApiClient.post(`/api/v4/jobs/${jobId}/applications/bulk_sms/add_to_event`, {
      event_id: eventId,
      applications_ids: applicationIds,
      content: content,
      sms_template_id: smsTemplateId,
      added: true
    });
  sendInviteSms = (
    jobId: number,
    eventTypeId: number,
    applicationIds: number[],
    content: string,
    smsTemplateId?: number
  ) =>
    RecruitApiClient.post(`/api/v4/jobs/${jobId}/applications/bulk_sms/invite_to_timeslot`, {
      event_type_id: eventTypeId,
      applications_ids: applicationIds,
      content: content,
      sms_template_id: smsTemplateId,
      invited: true
    });
  sendEmail = (
    jobId: number,
    eventId: number,
    emailTemplateId: number,
    content: string,
    applicationIds: number[]
  ) =>
    RecruitApiClient.post(`/api/v4/jobs/${jobId}/applications/email`, {
      email: {
        event_id: eventId,
        email_template_id: emailTemplateId,
        content: content,
        application_ids: applicationIds
      }
    });
  getApplicationActivities = (
    jobId: number,
    applicationId: number
  ): Promise<{ res: { activities: IActivity[] } }> =>
    RecruitApiClient.get(`/api/v4/jobs/${jobId}/applications/${applicationId}/activities`, {
      override_limit: true,
      limit: 100
    });
  getApprovalTemplates = (): Promise<{ res: { enabled_requisition_forms: IApprovalTemplate[] } }> =>
    RecruitApiClient.get(`/api/v4/requisition_forms/enabled_for_applications`);
  sendExternalEmails = (jobId: number, params: ISendExternalEmailParams) =>
    RecruitApiClient.post(`/api/v4/jobs/${jobId}/applications/bulk_external_email`, params);
  getSmartForms = (
    params: IGetSmartFormsParams
  ): Promise<{ res: { smart_forms: IFormsOption[] } }> =>
    RecruitApiClient.get(`/api/v4/smart_forms`, params);
  getFormQuestions = (params: {
    form_id: number;
    form_response_id?: number;
    enabled_only?: boolean;
  }): Promise<{ res: { fields: IApplicantQuestion[] } }> =>
    RecruitApiClient.get(`/api/v4/fields`, params);
  addForm = (jobId: number, applicationId: number, formId: number, params: any) =>
    RecruitApiClient.post(`/api/v4/jobs/${jobId}/applications/${applicationId}/form_responses`, {
      form_id: formId,
      form_response: { answers_attributes: params }
    });
  sms = (
    jobId: number,
    applicationId: number,
    params: { to: string; content: string; sms_template_id?: number }
  ) =>
    RecruitApiClient.post(`/api/v4/jobs/${jobId}/applications/${applicationId}/sms`, {
      sms: params
    });
  sendForm = (jobId: number, applicationId: number, params: any) =>
    RecruitApiClient.post(`/api/v4/jobs/${jobId}/applications/${applicationId}/send_form`, {
      send_form: params
    });
  sendFormBySMS = (jobId: number, applicationId: number, params: any) =>
    RecruitApiClient.post(`/api/v4/jobs/${jobId}/applications/${applicationId}/send_sms_form`, {
      send_sms_form: params
    });
  getCareAdvantageEvents = (
    jobId: number
  ): Promise<{
    res: { care_advantage_events: IEventType[]; has_care_advantage_integration: boolean };
  }> => RecruitApiClient.get(`/api/v4/jobs/${jobId}/auto_emails/care_advantage_events`);
  getApplicationAttachments = (
    jobId: number,
    applicationId: number
  ): Promise<{ res: { attachments: IAttachment[] } }> =>
    RecruitApiClient.get(`/api/v4/jobs/${jobId}/applications/${applicationId}/attachments`, null);
  deleteAttachment = (jobId: number, applicationId: number, attachmentId: number) =>
    RecruitApiClient.delete(
      `/api/v4/jobs/${jobId}/applications/${applicationId}/attachments/${attachmentId}`
    );
  getRecruiters = (): Promise<{ res: { users: IRecruiter[] } }> =>
    RecruitApiClient.get('/api/v4/calendar/users', {});
  getEventJobs = (): Promise<{ res: { jobs: IJob[] } }> =>
    RecruitApiClient.get('/api/v4/calendar/jobs', {});
  getEventTypes = (): Promise<{ res: { event_types: IEventType[] } }> =>
    RecruitApiClient.get('/api/v4/calendar/event_types', {});
  inviteByEmail = (params: IInviteByEmailParams) =>
    RecruitApiClient.post(`/api/v4/calendar/invitations`, params);
  previewAttachment = (
    jobId: number,
    applicationId: number,
    attachmentId: number,
    index: number
  ): Promise<{ res: IPreviewData }> =>
    RecruitApiClient.get(
      `/api/v4/jobs/${jobId}/applications/${applicationId}/attachments/${attachmentId}`,
      { index: index }
    );
  getVideoTimestamps = (assetId: number): Promise<{ res: { timestamps: ITimestamp[] } }> =>
    RecruitApiClient.get(`/api/v4/video_timestamp`, { asset_id: assetId });
  createVideoTimestamp = (assetId: number, timestampName: string, timestampValue: string) =>
    RecruitApiClient.post(`/api/v4/video_timestamp`, {
      asset_id: assetId,
      timestamp: { name: timestampName, value: timestampValue }
    });
  updateVideoTimestamp = (
    assetId: number,
    timestampId: number,
    timestampName: string,
    timestampValue: string
  ) =>
    RecruitApiClient.put(`/api/v4/video_timestamp/${timestampId}`, {
      asset_id: assetId,
      timestamp: { name: timestampName, value: timestampValue }
    });
  deleteVideoTimestamp = (assetId: number, timestampId: number) =>
    RecruitApiClient.delete(`/api/v4/video_timestamp/${timestampId}`, { asset_id: assetId });
  getApplicationTasks = (
    jobId: number,
    applicationId: number
  ): Promise<{ res: { tasks: IApplicationTask[] } }> =>
    RecruitApiClient.get(`/api/v4/jobs/${jobId}/applications/${applicationId}/tasks`);
  updateApplicationTask = (jobId: number, applicationId: number, taskId: number, params: any) =>
    RecruitApiClient.put(`/api/v4/jobs/${jobId}/applications/${applicationId}/tasks/${taskId}`, {
      task: params
    });
  getWorkflows = (applicationId: number) =>
    RecruitApiClient.get(`/api/v4/workflows?application_id=${applicationId}`);
  getAssignableUsers = () => RecruitApiClient.get(`/api/v4/tasks/assignable_users`);
  assignWorkflow = (applicationId: number, params: { id: number; user_id: number }) =>
    RecruitApiClient.post(`/api/v4/workflows?application_id=${applicationId}`, params);
  addTask = (jobId: number, applicationId: number, params: IAddTaskParams) =>
    RecruitApiClient.post(`/api/v4/jobs/${jobId}/applications/${applicationId}/tasks`, {
      task: params
    });
  deleteTask = (jobId: number, applicationId: number, taskId: number) =>
    RecruitApiClient.delete(`/api/v4/jobs/${jobId}/applications/${applicationId}/tasks/${taskId}`);
  getApplicationEvents = (
    jobId: number,
    applicationId: number
  ): Promise<{ res: { events: IEvent[] } }> =>
    RecruitApiClient.get(`/api/v4/jobs/${jobId}/applications/${applicationId}/events`);
  getFormResponses = (
    jobId: number,
    applicationId: number
  ): Promise<{ res: { form_responses: IFormResponseListItem[] } }> =>
    RecruitApiClient.get(
      `/api/v4/jobs/${jobId}/applications/${applicationId}/form_responses`,
      null
    );
  showFormResponse = (
    jobId: number,
    applicationId: number,
    formResponseId: number
  ): Promise<{ res: { question_and_answers: IAnswer[] } }> =>
    RecruitApiClient.get(
      `/api/v4/jobs/${jobId}/applications/${applicationId}/form_responses/${formResponseId}`
    );
  editFormResponse = (jobId: number, applicationId: number, formResponseId: number, params: any) =>
    RecruitApiClient.put(
      `/api/v4/jobs/${jobId}/applications/${applicationId}/form_responses/${formResponseId}`,
      {
        form_response: { answers_attributes: params }
      }
    );
  deleteFormResponse = (jobId: number, applicationId: number, formResponseId: number) =>
    RecruitApiClient.delete(
      `/api/v4/jobs/${jobId}/applications/${applicationId}/form_responses/${formResponseId}`
    );
  getVideoInterview = (ApplicationId: number): Promise<{ res: IVideoInterview }> =>
    RecruitApiClient.get(`/api/v4/video_interviews/${ApplicationId}`);
  deleteVideoInterview = (ApplicationId: number) =>
    RecruitApiClient.delete(`/api/v4/video_interviews/${ApplicationId}`);
  getVideoInterviewBookmarks = (
    ApplicationId: number
  ): Promise<{ res: { bookmarks: ITimestamp[] } }> =>
    RecruitApiClient.get(`/api/v4/video_interviews/${ApplicationId}/bookmarks`);
  deleteVideoInterviewBookmark = (ApplicationId: number, id: string) => {
    return RecruitApiClient.delete(`/api/v4/video_interviews/${ApplicationId}/bookmarks/${id}`);
  };
  createVideoInterviewBookmark = (ApplicationId: number, name: string, value: number) => {
    return RecruitApiClient.post(`/api/v4/video_interviews/${ApplicationId}/bookmarks`, {
      name,
      value
    });
  };
  updateVideoInterviewBookmark = (
    ApplicationId: number,
    id: string,
    name: string,
    value: number
  ) => {
    return RecruitApiClient.put(`/api/v4/video_interviews/${ApplicationId}/bookmarks/${id}`, {
      name,
      value
    });
  };
  exportToOnboard = (jobId: number, applicationId: number, startDate: Date) => {
    return RecruitApiClient.post(`/api/v4/jobs/${jobId}/scout_onboard_exports`, {
      start_date: dayjs(startDate).format('YYYY-MM-DD'),
      application_ids: [applicationId]
    });
  };
  getRevolveData = (applicationId: number) =>
    RecruitApiClient.get(`/api/v4/integrations/revolve_exports`, { application_id: applicationId });
  revolveExport = (applicationId: number, params: IRevolveExportParams) => {
    return RecruitApiClient.post(`/api/v4/integrations/revolve_exports`, {
      application_id: applicationId,
      revolve_record: params
    });
  };
  getXrefPackages = (applicationId: number) =>
    RecruitApiClient.get(`/api/v4/integrations/xref/packages`, { application_id: applicationId });
  xRefExport = (applicationId: number, params: IXRefExportParams) => {
    return RecruitApiClient.post(`/api/v4/integrations/xref`, {
      application_id: applicationId,
      ...params
    });
  };
  getIntegrationsData = (applicationId: number): Promise<{ res: IIntegrationsResponse }> =>
    RecruitApiClient.get(`/api/v4/integrations`, { application_id: applicationId });
  resolveUnusualActivity = (
    applicationId: number,
    params: { referee_id: number; note: string; reference_id: number; resolution?: number }
  ) => {
    return RecruitApiClient.post(`/api/v4/integrations/xref/resolve_unusual_activity`, {
      application_id: applicationId,
      ...params
    });
  };
  getCountriesStates = (): Promise<{ res: { countries: ICountries[] } }> =>
    RecruitApiClient.get('/api/v4/countries');
  getMvsData = (applicationId: number) =>
    RecruitApiClient.get(`/api/v4/integrations/mvs`, { application_id: applicationId });
  mvsExport = (applicationId: number, params: { package_id: string; blueprint_ids: string[] }) => {
    return RecruitApiClient.post(`/api/v4/integrations/mvs`, {
      application_id: applicationId,
      ...params
    });
  };
  getReferooData = (applicationId: number): Promise<{ res: IReferooData }> =>
    RecruitApiClient.get(`/api/v4/integrations/referoo`, { application_id: applicationId });
  referooExport = (applicationId: number, params: IReferooExportParams) => {
    return RecruitApiClient.post(`/api/v4/integrations/referoo`, {
      application_id: applicationId,
      ...params
    });
  };
  foundUExport = (applicationId: number, params: IFoundUExportParams) => {
    return RecruitApiClient.post(`/api/v4/integrations/foundu`, {
      application_id: applicationId,
      ...params
    });
  };
  elmoExport = (applicationId: number, params: IElmoExportParams) => {
    return RecruitApiClient.post(`/api/v4/integrations/elmo`, {
      application_id: applicationId,
      ...params
    });
  };
  getElmoDepartments = (): Promise<{ res: string[] }> =>
    RecruitApiClient.get('/api/v4/integrations/elmo/departments', null);
  getTestGridPackages = (applicationId: number): Promise<{ res: Record<string, string> }> =>
    RecruitApiClient.get(`/api/v4/integrations/test_grids/packages`, {
      application_id: applicationId
    });
  testGridExport = (applicationId: number, packageId: string) => {
    return RecruitApiClient.post(`/api/v4/integrations/test_grids`, {
      application_id: applicationId,
      package_id: packageId
    });
  };
  getHronboardData = (applicationId: number): Promise<{ res: IHronboardResponse }> =>
    RecruitApiClient.get(`/api/v4/integrations/hro_exports`, { application_id: applicationId });
  hronboardExport = (
    applicationId: number,
    params: IHronboardExportParams
  ): Promise<{ res: { message: string } }> => {
    return RecruitApiClient.post(`/api/v4/integrations/hro_exports`, {
      application_id: applicationId,
      hro_record: params
    });
  };
  roublerExport = (applicationId: number, params: IRoublerExportParams) => {
    return RecruitApiClient.post(`/api/v4/integrations/roubler_exports`, {
      application_id: applicationId,
      roubler_record: params
    });
  };
  getFit2WorkData = (applicationId: number): Promise<{ res: IFit2WorkData }> =>
    RecruitApiClient.get(`/api/v4/integrations/fit2work`, { application_id: applicationId });
  fit2WorkExport = (
    applicationId: number,
    params: IFit2WorkExportParams
  ): Promise<{ res: { success: string } }> => {
    return RecruitApiClient.post(`/api/v4/integrations/fit2work`, {
      application_id: applicationId,
      ...params
    });
  };
  getSparkHireData = (applicationId: number): Promise<{ res: ISparkHireData }> =>
    RecruitApiClient.get(`/api/v4/integrations/sparkhire_exports`, {
      application_id: applicationId
    });
  sparkHireExport = (
    applicationId: number,
    params: ISparkHireExportParams
  ): Promise<{ res: { success: string } }> => {
    return RecruitApiClient.post(`/api/v4/integrations/sparkhire_exports`, {
      application_id: applicationId,
      sparkhire_record: params
    });
  };
  getAlayaCareData = (
    applicationId: number
  ): Promise<{ res: { genders: string[]; states: string[]; titles: string[] } }> =>
    RecruitApiClient.get(`/api/v4/integrations/alayacare`, { application_id: applicationId });
  alayaCareExport = (params: IAlayaCareExportParams): Promise<{ res: { success: string } }> => {
    return RecruitApiClient.post(`/api/v4/integrations/alayacare`, params);
  };
  updateFit2WorkStatus = (applicationId: number, referenceId: number) =>
    RecruitApiClient.get(`/api/v4/integrations/fit2work/${referenceId}/update_status`, {
      application_id: applicationId
    });
  getCognologyData = (applicationId: number): Promise<{ res: ICognologyData }> =>
    RecruitApiClient.get(`/api/v4/integrations/cognology_exports`, {
      application_id: applicationId
    });
  cognologyExport = (
    params: ICognologyExportParams
  ): Promise<{ res: { success: string; approval_url?: string } }> => {
    return RecruitApiClient.post(`/api/v4/integrations/cognology_exports`, {
      cognology_record: params
    });
  };
  exportToCognology = (applicationId: number): Promise<{ res: { success: string } }> => {
    return RecruitApiClient.post(`/api/v4/integrations/cognology_exports/export`, {
      application_id: applicationId
    });
  };
  getApprovalForms = (): Promise<{ res: { requisitions: IApprovalForm[] } }> =>
    RecruitApiClient.get(`/api/v4/requisitions`, { override_limit: true, limit: 1000 });
  getApprovalForm = (approvalFormId: number): Promise<{ res: IApprovalForm }> =>
    RecruitApiClient.get(`/api/v4/requisitions/${approvalFormId}`);
  getIntelliHRData = (applicationId: number): Promise<{ res: IIntelliHrData }> =>
    RecruitApiClient.get(`/api/v4/integrations/intellihr`, { application_id: applicationId });
  exportToIntelliHR = (applicationId: number, params: any) => {
    return RecruitApiClient.post(`/api/v4/integrations/intellihr`, {
      application_id: applicationId,
      intellihr: params
    });
  };
}

export default new Api();
